import { useEffect, useMemo, useState } from "react";
import {
  fetchCasaMapaAdminListing,
  patchCasaMapaAdminListing,
} from "../../../api/clientCasamapaAdminApi";
import { useLocale } from "../../../intl";

const COPY = "realEstate.casaMapa.admin.listings.repair";

const EMPTY_FORM = {
  description: "",
  priceCop: "",
  bedrooms: "",
  bathrooms: "",
  areaSqm: "",
  neighborhood: "",
  municipality: "",
  latitude: "",
  longitude: "",
};

function formFromListing(listing) {
  return {
    description: listing?.description || "",
    priceCop: listing?.priceCop ?? "",
    bedrooms: listing?.bedrooms ?? "",
    bathrooms: listing?.bathrooms ?? "",
    areaSqm: listing?.areaSqm ?? "",
    neighborhood: listing?.neighborhood || "",
    municipality: listing?.municipality || "",
    latitude: listing?.latitude ?? "",
    longitude: listing?.longitude ?? "",
  };
}

function Field({ label, hint, children, highlight = false }) {
  return (
    <label className={`cm-listing-repair__field${highlight ? " is-highlight" : ""}`}>
      <span>{label}</span>
      {children}
      {hint ? <small>{hint}</small> : null}
    </label>
  );
}

export default function CasaMapaListingRepairDrawer({
  clientViewId,
  listingId,
  onClose,
  onSaved,
}) {
  const { t } = useLocale();
  const [listing, setListing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (!clientViewId || !listingId) return;

    let cancelled = false;
    setLoading(true);
    setError("");
    setMessage("");

    fetchCasaMapaAdminListing(clientViewId, listingId)
      .then((result) => {
        if (cancelled) return;
        const nextListing = result.listing || null;
        setListing(nextListing);
        setForm(formFromListing(nextListing));
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err.message || t(`${COPY}.loadError`));
          setListing(null);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [clientViewId, listingId, t]);

  const issueSet = useMemo(() => new Set(listing?.issueIds || []), [listing?.issueIds]);

  const updateField = (key, value) => {
    setForm((current) => ({ ...current, [key]: value }));
  };

  const save = async ({ confirmAvailability = false, rebuildFromSources = false } = {}) => {
    if (!clientViewId || !listingId) return;
    try {
      setSaving(true);
      setError("");
      setMessage("");
      const payload = {
        description: form.description,
        priceCop: form.priceCop === "" ? null : Number(form.priceCop),
        bedrooms: form.bedrooms === "" ? null : Number(form.bedrooms),
        bathrooms: form.bathrooms === "" ? null : Number(form.bathrooms),
        areaSqm: form.areaSqm === "" ? null : Number(form.areaSqm),
        neighborhood: form.neighborhood,
        municipality: form.municipality,
        confirmAvailability,
        rebuildFromSources,
      };

      if (form.latitude !== "" || form.longitude !== "") {
        payload.latitude = form.latitude === "" ? null : Number(form.latitude);
        payload.longitude = form.longitude === "" ? null : Number(form.longitude);
      }

      const result = await patchCasaMapaAdminListing(clientViewId, listingId, payload);
      const nextListing = result.listing || null;
      setListing(nextListing);
      setForm(formFromListing(nextListing));
      setMessage(t(`${COPY}.saved`));
      onSaved?.(nextListing);
    } catch (err) {
      setError(err.message || t(`${COPY}.saveError`));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="cm-listing-repair" role="dialog" aria-modal="true" aria-labelledby="cm-listing-repair-title">
      <button type="button" className="cm-listing-repair__backdrop" onClick={onClose} aria-label={t(`${COPY}.close`)} />
      <section className="cm-listing-repair__panel">
        <header className="cm-listing-repair__header">
          <div>
            <p className="eyebrow">{t(`${COPY}.eyebrow`)}</p>
            <h2 id="cm-listing-repair-title">{listing?.title || t(`${COPY}.title`)}</h2>
            {listing ? (
              <p className="muted">
                {[listing.municipality, listing.neighborhood].filter(Boolean).join(", ") || listing.agencyName}
              </p>
            ) : null}
          </div>
          <button type="button" className="cm-listing-repair__close" onClick={onClose}>
            {t(`${COPY}.close`)}
          </button>
        </header>

        {loading ? <p className="muted">{t(`${COPY}.loading`)}</p> : null}
        {error ? <p className="admin-data-health-error">{error}</p> : null}
        {message ? <p className="cm-listing-repair__message">{message}</p> : null}

        {!loading && listing ? (
          <>
            <div className="cm-listing-repair__summary">
              {listing.photoUrl ? (
                <img src={listing.photoUrl} alt="" className="cm-listing-repair__photo" />
              ) : (
                <span className="cm-listing-repair__photo cm-listing-repair__photo--placeholder" aria-hidden="true">
                  ⌂
                </span>
              )}
              <div>
                <div className="cm-listing-repair__issue-tags">
                  {listing.issueIds.map((issueId) => (
                    <span key={issueId} className="cm-listing-health__issue-tag cm-listing-health__issue-tag--active">
                      {t(`realEstate.casaMapa.admin.listings.issues.${issueId}`)}
                    </span>
                  ))}
                </div>
                <div className="cm-listing-repair__meta">
                  <span>
                    {t("realEstate.casaMapa.admin.listings.table.completeness")}: {listing.completenessScore ?? "—"}%
                  </span>
                  <span>
                    {t("realEstate.casaMapa.admin.listings.table.freshness")}:{" "}
                    {t(`realEstate.casaMapa.admin.listings.freshness.${listing.freshnessStatus || "unknown"}`)}
                  </span>
                </div>
                {listing.publicUrl ? (
                  <a href={listing.publicUrl} target="_blank" rel="noreferrer">
                    {t("realEstate.casaMapa.admin.viewPublic")}
                  </a>
                ) : null}
              </div>
            </div>

            <form
              className="cm-listing-repair__form"
              onSubmit={(event) => {
                event.preventDefault();
                save();
              }}
            >
              <Field
                label={t(`${COPY}.fields.description`)}
                highlight={issueSet.has("missing_description") || issueSet.has("partial_profile")}
              >
                <textarea
                  rows={4}
                  value={form.description}
                  onChange={(event) => updateField("description", event.target.value)}
                />
              </Field>

              <div className="cm-listing-repair__grid">
                <Field label={t(`${COPY}.fields.price`)} highlight={issueSet.has("missing_price")}>
                  <input
                    type="number"
                    min="0"
                    value={form.priceCop}
                    onChange={(event) => updateField("priceCop", event.target.value)}
                  />
                </Field>
                <Field label={t(`${COPY}.fields.area`)} highlight={issueSet.has("missing_area")}>
                  <input
                    type="number"
                    min="0"
                    value={form.areaSqm}
                    onChange={(event) => updateField("areaSqm", event.target.value)}
                  />
                </Field>
                <Field label={t(`${COPY}.fields.bedrooms`)} highlight={issueSet.has("missing_beds_baths")}>
                  <input
                    type="number"
                    min="0"
                    value={form.bedrooms}
                    onChange={(event) => updateField("bedrooms", event.target.value)}
                  />
                </Field>
                <Field label={t(`${COPY}.fields.bathrooms`)} highlight={issueSet.has("missing_beds_baths")}>
                  <input
                    type="number"
                    min="0"
                    step="0.5"
                    value={form.bathrooms}
                    onChange={(event) => updateField("bathrooms", event.target.value)}
                  />
                </Field>
              </div>

              <div className="cm-listing-repair__grid">
                <Field
                  label={t(`${COPY}.fields.municipality`)}
                  highlight={issueSet.has("missing_location")}
                >
                  <input
                    value={form.municipality}
                    onChange={(event) => updateField("municipality", event.target.value)}
                  />
                </Field>
                <Field
                  label={t(`${COPY}.fields.neighborhood`)}
                  highlight={issueSet.has("missing_location")}
                >
                  <input
                    value={form.neighborhood}
                    onChange={(event) => updateField("neighborhood", event.target.value)}
                  />
                </Field>
              </div>

              <div className="cm-listing-repair__grid">
                <Field
                  label={t(`${COPY}.fields.latitude`)}
                  highlight={issueSet.has("unmapped_coordinates")}
                >
                  <input
                    type="number"
                    step="any"
                    value={form.latitude}
                    onChange={(event) => updateField("latitude", event.target.value)}
                  />
                </Field>
                <Field
                  label={t(`${COPY}.fields.longitude`)}
                  highlight={issueSet.has("unmapped_coordinates")}
                >
                  <input
                    type="number"
                    step="any"
                    value={form.longitude}
                    onChange={(event) => updateField("longitude", event.target.value)}
                  />
                </Field>
              </div>

              <div className="cm-listing-repair__actions">
                <button type="submit" className="midpoint-share-button" disabled={saving}>
                  {saving ? t(`${COPY}.saving`) : t(`${COPY}.save`)}
                </button>
                {(issueSet.has("stale") || issueSet.has("aging")) && (
                  <button
                    type="button"
                    className="casamapa-admin-link-button"
                    disabled={saving}
                    onClick={() => save({ confirmAvailability: true })}
                  >
                    {t(`${COPY}.confirmAvailability`)}
                  </button>
                )}
                <button
                  type="button"
                  className="cm-listing-repair__secondary"
                  disabled={saving}
                  onClick={() => save({ rebuildFromSources: true })}
                >
                  {t(`${COPY}.rebuild`)}
                </button>
              </div>
            </form>
          </>
        ) : null}
      </section>
    </div>
  );
}
